import { useParams, Link, Navigate } from "react-router-dom";
import { useState } from "react";
import { Check, ShieldAlert, MapPin } from "lucide-react";
import { VEHICLES } from "@/data/site";
import { Lightbox } from "@/components/Lightbox";
import { QuickQuote } from "@/components/QuickQuote";
import { ShareButtons } from "@/components/ShareButtons";
import { RateCalculator } from "@/components/RateCalculator";

export default function VehicleDetail() {
  const { code } = useParams();
  const v = VEHICLES[code];
  const [lb, setLb] = useState(null);
  if (!v) return <Navigate to="/" replace />;

  return (
    <div>
      <section className="grid md:grid-cols-2 gap-0 border-b border-black/10">
        <div className="p-8 md:p-16 flex flex-col justify-center">
          <div className="overline text-[#BA5A3A]">{v.path === "adventure" ? "Adventure" : "Premium Family"}</div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tighter mt-3">{v.name}</h1>
          <p className="text-lg text-[#595959] mt-4 max-w-lg">{v.tagline}</p>
          <div className="mt-8 grid grid-cols-3 gap-4 text-sm">
            <Fact label="Seats" value={v.seats} />
            <Fact label="Min hire" value={`${v.min_days} days`} />
            <Fact label="Min age" value={v.min_age} />
          </div>
          <ShareButtons title={v.name} />
        </div>
        <div className="relative min-h-[420px]">
          <img src={v.gallery[0]} alt={v.name} onClick={() => setLb(0)} className="absolute inset-0 w-full h-full object-cover cursor-zoom-in" data-testid="hero-image" />
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-12">
          <Block title="Daily rate">
            <div className="flex items-baseline gap-4 flex-wrap">
              <div>
                <div className="overline text-[#595959]">Flat daily rate</div>
                <div className="text-4xl font-black mt-1">A${v.daily_rate}<span className="text-lg text-[#595959] font-medium">/day</span></div>
              </div>
              <div className="pl-6 border-l border-black/10">
                <div className="overline text-[#595959]">Kilometre allowance</div>
                <div className="text-sm mt-1"><strong>250 km/day</strong> for standard hires · over-cap billed at <strong>A$0.35/km</strong> from bond.<br/><strong className="text-[#BA5A3A]">Unlimited kilometres</strong> when you book 5+ days.</div>
              </div>
            </div>
            <p className="text-xs text-[#595959] mt-4">Minimum hire {v.min_days} days. 10% off weekly hires (7+ days). Bond and excess disclosed below.</p>
          </Block>

          <section>
            <h2 className="text-2xl font-black tracking-tight flex items-center gap-2">Work out your rate</h2>
            <RateCalculator vehicle={v} />
          </section>

          <Block title="Inclusions" icon={Check}>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {v.inclusions.map(i => (
                <li key={i} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-[#BA5A3A] mt-0.5 flex-shrink-0"/>{i}</li>
              ))}
            </ul>
          </Block>

          <Block title="Bond, excess and geographic restrictions" icon={ShieldAlert}>
            <div className="grid sm:grid-cols-2 gap-6 text-sm">
              <div>
                <div className="overline text-[#595959]">Bond (pre-authorised at pickup)</div>
                <div className="mt-2 text-2xl font-black">A${v.bond_standard}</div>
                <div className="text-[#595959]">or A${v.bond_reduced} with excess reduction (A${v.excess_reduction_per_day}/day)</div>
              </div>
              <div>
                <div className="overline text-[#595959]">Deposit to confirm</div>
                <div className="mt-2 text-2xl font-black">25%</div>
                <div className="text-[#595959]">Payment link sent once we confirm availability. Non-refundable within 14 days of pickup.</div>
              </div>
            </div>
            <div className="mt-6">
              <div className="overline text-[#595959] flex items-center gap-2"><MapPin className="w-3.5 h-3.5"/> Where you can drive</div>
              <ul className="mt-2 space-y-1">
                {v.restrictions.map(r => (<li key={r} className="text-sm text-[#1C1C1C]">— {r}</li>))}
              </ul>
            </div>
          </Block>

          <Block title="How booking works">
            <ol className="space-y-3 text-sm list-decimal pl-5">
              <li>Send us your dates via the enquiry form or WhatsApp.</li>
              <li>We confirm availability and reply within 24 hrs with a quote.</li>
              <li>Pay the 25% deposit via the secure payment link we send you.</li>
              <li>Bring your licence and a second ID at pickup — bond is pre-authorised on your card.</li>
            </ol>
          </Block>
        </div>

        <aside className="lg:col-span-1">
          <div className="card-hard sticky top-24 p-6">
            <div className="overline text-[#BA5A3A]">Ready?</div>
            <div className="text-2xl font-black mt-2 leading-tight">Request the {v.name.split(" ").slice(-2).join(" ")}</div>
            <p className="text-sm text-[#595959] mt-2">Tell us your dates. Manual review inside 24 hrs — deposit only requested after we confirm.</p>
            <Link to={`/enquiry?vehicle=${v.code}`} className="btn-ochre w-full justify-center mt-6" data-testid="request-booking-btn">
              Request booking
            </Link>
            <a href={`https://wa.me/61472577752?text=${encodeURIComponent(`Hi, I'd like to book the ${v.name}.`)}`} target="_blank" rel="noreferrer" className="btn-ghost w-full justify-center !mt-3 block text-center" data-testid="whatsapp-booking-btn">
              WhatsApp us
            </a>
            <QuickQuote vehicle={v} />
            <p className="text-xs text-[#595959] mt-4">Pickup address is disclosed after deposit is paid.</p>
          </div>
        </aside>
      </div>

      <section className="max-w-7xl mx-auto px-6 pb-24 grid md:grid-cols-3 gap-4">
        {v.gallery.slice(1).map((g, i) => (
          <img key={i} src={g} alt="" onClick={() => setLb(i + 1)} className="w-full h-64 object-cover border border-black/10 cursor-zoom-in hover:opacity-90 transition-opacity" data-testid="gallery-image"/>
        ))}
      </section>
      {lb !== null && <Lightbox images={v.gallery} index={lb} onClose={() => setLb(null)} />}
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div className="border-l-2 border-[#BA5A3A] pl-3">
      <div className="overline text-[#595959]">{label}</div>
      <div className="text-xl font-black mt-1">{value}</div>
    </div>
  );
}
function Block({ title, icon: Icon, children }) {
  return (
    <section>
      <h2 className="text-2xl font-black tracking-tight flex items-center gap-2">
        {Icon && <Icon className="w-5 h-5 text-[#BA5A3A]" />}{title}
      </h2>
      <div className="mt-4 card-hard p-6">{children}</div>
    </section>
  );
}
